<template>
    <div id="page-profile" data-page="profile" class="profile-page py-4">
        <b-container>
            <!-- Page Header -->
            <div class="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                <div>
                    <h3 class="font-weight-bold text-dark mb-1">
                        <i class="bi bi-person-gear text-primary mr-2"></i>Profile & Preferences
                    </h3>
                    <div class="text-muted small">Manage your identity, visual appearance, localization, and subscription</div>
                </div>
            </div>

            <!-- Identity Card -->
            <b-card no-body class="shadow-sm border-0 mb-4 overflow-hidden">
                <b-card-body class="p-4">
                    <div class="d-flex flex-wrap align-items-center justify-content-between">
                        <div class="d-flex align-items-center mb-3 mb-md-0 mr-md-3" style="flex: 1 1 280px; min-width: 0;">
                            <div class="profile-avatar-wrapper mr-3 position-relative">
                                <b-avatar :src="profileAvatarUrl"
                                          :text="!profileAvatarUrl ? avatarInitials : undefined"
                                          size="76px"
                                          variant="primary"
                                          class="shadow-sm border profile-avatar"></b-avatar>
                                <button type="button"
                                        class="avatar-upload-overlay"
                                        @click="triggerAvatarUpload"
                                        :disabled="uploadingAvatar"
                                        title="Change Avatar"
                                        aria-label="Change Avatar"
                                        data-test="btn-change-avatar">
                                    <b-spinner v-if="uploadingAvatar" small></b-spinner>
                                    <i v-else class="bi bi-camera-fill"></i>
                                </button>
                                <input type="file"
                                       ref="avatarFileInput"
                                       class="d-none"
                                       accept="image/png,image/jpeg,image/webp,image/gif"
                                       @change="onAvatarFileSelected" />
                            </div>
                            <div>
                                <h4 class="font-weight-bold text-dark mb-1">{{ profileData.fullName || account.fullName || 'User' }}</h4>
                                <div class="text-muted mb-1"><i class="bi bi-envelope mr-1"></i>{{ profileData.email || account.email }}</div>
                                <div class="d-flex align-items-center flex-wrap mt-1">
                                    <span class="badge badge-light border text-muted small mr-2">UID: {{ profileData.uid || account.uid || 'N/A' }}</span>
                                    <b-button size="sm"
                                              variant="outline-secondary"
                                              class="py-0 px-2 small font-weight-normal mr-1 btn-avatar-action"
                                              @click="triggerAvatarUpload"
                                              :disabled="uploadingAvatar"
                                              data-test="btn-upload-pic">
                                        <i class="bi bi-upload mr-1"></i>Upload Photo
                                    </b-button>
                                    <b-button v-if="hasCustomAvatar"
                                              size="sm"
                                              variant="outline-danger"
                                              class="py-0 px-2 small font-weight-normal btn-avatar-action"
                                              @click="removeAvatar"
                                              :disabled="uploadingAvatar"
                                              data-test="btn-remove-avatar"
                                              title="Reset to default avatar">
                                        <i class="bi bi-trash mr-1"></i>Remove
                                    </b-button>
                                </div>
                            </div>
                        </div>
                        <div class="text-md-right ml-md-auto flex-shrink-0">
                            <div class="d-flex align-items-center justify-content-md-end">
                                <span v-if="isBetaUser" class="badge badge-beta-amber font-weight-bold mr-2 px-2 py-2" data-test="badge-beta-member">
                                    <i class="fa-solid fa-flask mr-1"></i>Beta Member
                                </span>
                                <span class="badge badge-primary px-3 py-2 text-uppercase font-weight-bold mr-2" data-test="badge-current-plan">
                                    <i class="fa-solid fa-crown mr-1"></i>{{ currentPlanName }}
                                </span>
                                <b-button variant="outline-primary" size="sm" to="/plans" data-test="btn-view-plans">
                                    <i class="bi bi-arrow-up-circle mr-1"></i>View Plans
                                </b-button>
                            </div>
                            <div v-if="workspace" class="small text-muted mt-2">
                                Workspace: <strong>{{ workspace.name }}</strong>
                            </div>
                        </div>
                    </div>
                </b-card-body>
            </b-card>

            <!-- Appearance Card -->
            <b-card no-body class="shadow-sm border-0 mb-4">
                <div class="card-header bg-transparent border-bottom py-3">
                    <h5 class="font-weight-bold text-dark mb-0">
                        <i class="bi bi-palette-fill text-info mr-2"></i>Appearance & Visual Theme
                    </h5>
                </div>
                <b-card-body class="p-4">
                    <p class="text-muted small mb-3">Choose how VelocyTrack looks to you. Changes take effect across all pages immediately.</p>
                    <b-row>
                        <b-col sm="6" md="6" lg="4" class="mb-3">
                            <div class="theme-option-card p-3 rounded border text-center"
                                 :class="{ 'theme-option-active': selectedTheme === 'light' }"
                                 role="button"
                                 data-test="theme-option-light"
                                 :aria-pressed="selectedTheme === 'light' ? 'true' : 'false'"
                                 @click="changeTheme('light')">
                                 <div class="theme-preview-box theme-light-box mb-2">
                                    <i class="bi bi-sun-fill text-warning display-4"></i>
                                </div>
                                <div class="font-weight-bold">Light Theme</div>
                                <div class="small text-muted">Clean daylight mode</div>
                            </div>
                        </b-col>
                        <b-col sm="6" md="6" lg="4" class="mb-3">
                            <div class="theme-option-card p-3 rounded border text-center"
                                 :class="{ 'theme-option-active': selectedTheme === 'dark' }"
                                 role="button"
                                 data-test="theme-option-dark"
                                 :aria-pressed="selectedTheme === 'dark' ? 'true' : 'false'"
                                 @click="changeTheme('dark')">
                                <div class="theme-preview-box theme-dark-box mb-2">
                                    <i class="bi bi-moon-stars-fill text-info display-4"></i>
                                </div>
                                <div class="font-weight-bold">Dark Theme</div>
                                <div class="small text-muted">Sleek low-glare dark mode</div>
                            </div>
                        </b-col>
                    </b-row>
                </b-card-body>
            </b-card>

            <!-- Localization Card -->
            <b-card no-body class="shadow-sm border-0 mb-4">
                <div class="card-header bg-transparent border-bottom py-3 d-flex justify-content-between align-items-center">
                    <h5 class="font-weight-bold text-dark mb-0">
                        <i class="bi bi-globe-americas text-success mr-2"></i>Localization & Holiday Calendar
                    </h5>
                    <b-button size="sm" variant="outline-secondary" @click="autoDetectLocale" data-test="btn-autodetect-locale">
                        <i class="bi bi-crosshair mr-1"></i>Auto-Detect from Browser
                    </b-button>
                </div>
                <b-card-body class="p-4">
                    <b-row>
                        <b-col md="6" class="mb-3">
                            <b-form-group label="Public Holidays Country" description="Select which statutory public holidays to display in Timelog and calendars.">
                                <b-form-select v-model="selectedCountryCode" :options="countryOptions" @change="onCountryChange" data-test="select-country">
                                </b-form-select>
                            </b-form-group>
                        </b-col>
                        <b-col md="6" class="mb-3">
                            <b-form-group label="Timezone" description="Display-layer timezone for times and dates.">
                                <b-form-select v-model="selectedTimezone" :options="timezoneOptions" @change="onTimezoneChange" data-test="select-timezone">
                                </b-form-select>
                            </b-form-group>
                        </b-col>
                    </b-row>
                </b-card-body>
            </b-card>

            <!-- Work Defaults Card -->
            <b-card no-body class="shadow-sm border-0 mb-4">
                <div class="card-header bg-transparent border-bottom py-3">
                    <h5 class="font-weight-bold text-dark mb-0">
                        <i class="bi bi-briefcase-fill text-primary mr-2"></i>Work & Logging Defaults
                    </h5>
                </div>
                <b-card-body class="p-4">
                    <p class="text-muted small mb-3">Configure personal defaults for time logging, daily capacity, and role.</p>
                    <b-row>
                        <b-col md="6" lg="3" class="mb-3">
                            <b-form-group label="Start Log Date" description="The starting date from which timesheet entries are tracked.">
                                <b-form-input v-model="startLogDate" type="date" data-test="input-start-log-date"></b-form-input>
                            </b-form-group>
                        </b-col>
                        <b-col md="6" lg="3" class="mb-3">
                            <b-form-group label="Daily Capacity (Hours)" description="Standard working hours per day (1 - 12).">
                                <b-form-input v-model.number="dailyCapacity" type="number" min="1" max="12" data-test="input-daily-capacity"></b-form-input>
                            </b-form-group>
                        </b-col>
                        <b-col md="6" lg="3" class="mb-3">
                            <b-form-group label="Default Role" description="Your default discipline for work item effort.">
                                <b-form-select v-model.number="defaultRole" data-test="select-default-role">
                                    <b-form-select-option :value="0">Developer</b-form-select-option>
                                    <b-form-select-option :value="1">QA</b-form-select-option>
                                    <b-form-select-option :value="2">System Analyst</b-form-select-option>
                                </b-form-select>
                            </b-form-group>
                        </b-col>
                        <b-col md="6" lg="3" class="mb-3">
                            <b-form-group label="Discussions Lookback Period" description="Discussions on assigned items window (1 - 30 days).">
                                <b-form-input v-model.number="inboxAssignedDays" type="number" min="1" max="30" data-test="input-inbox-days"></b-form-input>
                            </b-form-group>
                        </b-col>
                    </b-row>
                    <div class="d-flex justify-content-end">
                        <b-button variant="primary" :disabled="savingWorkDefaults" @click="saveWorkDefaults" data-test="btn-save-work-defaults" :data-state="savingWorkDefaults ? 'loading' : 'idle'">
                            <b-spinner v-if="savingWorkDefaults" small class="mr-1"></b-spinner>
                            Save Work Defaults
                        </b-button>
                    </div>
                </b-card-body>
            </b-card>

            <!-- Reminders & Automated Notifications Card -->
            <b-card id="card-reminders" no-body class="shadow-sm border-0 mb-4">
                <div class="card-header bg-transparent border-bottom py-3 d-flex justify-content-between align-items-center">
                    <div class="d-flex align-items-center flex-wrap">
                        <h5 class="font-weight-bold text-dark mb-0">
                            <i class="bi bi-bell-fill text-warning mr-2"></i>Reminders &amp; Email Notifications
                        </h5>
                        <b-badge v-if="hasReminderMonthlyQuota" :variant="isReminderQuotaExhausted ? 'warning' : 'info'" :class="['font-weight-bold', 'ml-2', 'px-2', 'py-1', isReminderQuotaExhausted ? 'text-dark' : '']" data-test="badge-reminders-quota">
                            <i class="bi bi-envelope-paper-fill mr-1"></i>{{ remindersUsedThisMonth }}/{{ remindersLimitThisMonth }} FREE THIS MONTH
                        </b-badge>
                    </div>
                    <b-badge v-if="reminderSettings.enableDailyReminder || reminderSettings.enableWeeklyReminder || reminderSettings.behindHoursThreshold > 0" variant="success" class="px-2 py-1" data-test="badge-reminders-active">
                        Active
                    </b-badge>
                    <b-badge v-else variant="light" class="text-muted border px-2 py-1" data-test="badge-reminders-disabled">
                        Disabled
                    </b-badge>
                </div>
                <b-card-body class="p-4">
                    <!-- Monthly Reminder Quota callout for Free tier -->
                    <div v-if="hasReminderMonthlyQuota" :class="['alert', isReminderQuotaExhausted ? 'alert-warning' : 'alert-info', 'd-flex', 'flex-wrap', 'align-items-center', 'justify-content-between', 'p-3', 'mb-3']" data-test="alert-reminders-quota">
                        <div class="mr-3" style="flex: 1 1 280px;">
                            <i :class="['bi', isReminderQuotaExhausted ? 'bi-exclamation-triangle-fill' : 'bi-info-circle-fill', 'mr-2']"></i>
                            <template v-if="isReminderQuotaExhausted">
                                <strong>Monthly Free Quota Reached ({{ remindersUsedThisMonth }} / {{ remindersLimitThisMonth }} used):</strong> Automated email reminders are paused for the rest of this calendar month. Upgrade to the <strong>Pro Plan</strong> for unlimited reminders.
                            </template>
                            <template v-else>
                                <strong>Free Tier Quota ({{ remindersUsedThisMonth }} / {{ remindersLimitThisMonth }} free reminders used this month):</strong> You have <strong>{{ remindersRemainingThisMonth }}</strong> automated email {{ remindersRemainingThisMonth === 1 ? 'reminder' : 'reminders' }} left this month (resets on the 1st of next month). Upgrade to the <strong>Pro Plan</strong> for unlimited reminders.
                            </template>
                        </div>
                        <b-button size="sm" variant="warning" class="font-weight-bold text-dark mt-2 mt-md-0 shadow-sm text-nowrap"
                            data-test="btn-upgrade-reminders"
                            to="/plans?reason=Unlimited+Automated+Email+Reminders&requiredPlan=Pro+Plan">
                            <i class="bi bi-arrow-up-circle mr-1"></i>Upgrade Plan
                        </b-button>
                    </div>
                    <p class="text-muted small mb-3">
                        Configure automated email notifications to help you stay caught up with your daily and project-level capacity.
                    </p>

                    <div v-if="profileData && profileData.email" class="alert alert-secondary py-2 px-3 small d-flex align-items-center mb-4">
                        <i class="bi bi-envelope-check text-primary mr-2" style="font-size: 1.1rem;"></i>
                        <span>Reminders will be sent to <strong>{{ profileData.email }}</strong>.</span>
                    </div>

                    <b-row class="mb-3">
                        <b-col md="4" class="mb-3">
                            <div class="p-3 border rounded h-100 reminder-switch-box">
                                <b-form-checkbox v-model="reminderSettings.enableDailyReminder" switch class="font-weight-bold mb-1" data-test="switch-daily-reminder">
                                    End-of-Day Reminder
                                </b-form-checkbox>
                                <p class="text-muted small mb-0 pl-4">
                                    Sends an email reminder on weekdays at your chosen hour if you have unlogged capacity on any active project.
                                </p>
                            </div>
                        </b-col>
                        <b-col md="4" class="mb-3">
                            <div class="p-3 border rounded h-100 reminder-switch-box">
                                <b-form-checkbox v-model="reminderSettings.enableWeeklyReminder" switch class="font-weight-bold mb-1" data-test="switch-weekly-reminder">
                                    End-of-Week Summary
                                </b-form-checkbox>
                                <p class="text-muted small mb-0 pl-4">
                                    Sends a Friday summary of your weekly capacity progress and any unlogged hours across projects.
                                </p>
                            </div>
                        </b-col>
                        <b-col md="4" class="mb-3">
                            <div class="p-3 border rounded h-100 reminder-switch-box d-flex flex-column justify-content-between">
                                <div>
                                    <b-form-checkbox v-model="enableBehindAlert" switch class="font-weight-bold mb-1" data-test="switch-behind-alert">
                                        Behind-Hours Alert
                                    </b-form-checkbox>
                                    <p class="text-muted small mb-2 pl-4">
                                        Triggers an alert whenever your unlogged hours reach or exceed this threshold.
                                    </p>
                                </div>
                                <div class="pl-4 mt-2">
                                    <b-input-group size="sm" append="hrs" prepend="Threshold">
                                        <b-form-input
                                            type="number"
                                            min="1"
                                            max="160"
                                            step="1"
                                            v-model.number="customBehindHours"
                                            :disabled="!enableBehindAlert"
                                            placeholder="8"
                                            data-test="input-behind-hours"
                                        ></b-form-input>
                                    </b-input-group>
                                </div>
                            </div>
                        </b-col>
                    </b-row>

                    <b-row>
                        <b-col md="6" class="mb-3">
                            <b-form-group
                                label="Preferred Reminder Send Time"
                                :description="hasAnyReminderActive ? `Local hour in your configured timezone (${selectedTimezone}).` : 'Disabled because all reminder switches above are currently turned off.'"
                            >
                                <b-form-select
                                    v-model.number="reminderSettings.reminderHourLocal"
                                    :options="reminderHourOptions"
                                    :disabled="!hasAnyReminderActive"
                                    data-test="select-reminder-hour"
                                ></b-form-select>
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <div class="d-flex justify-content-end mt-2">
                        <b-button variant="primary" :disabled="savingReminders" @click="saveReminderSettings" data-test="btn-save-reminders" :data-state="savingReminders ? 'loading' : 'idle'">
                            <b-spinner v-if="savingReminders" small class="mr-1"></b-spinner>
                            Save Reminder Settings
                        </b-button>
                    </div>
                </b-card-body>
            </b-card>

            <!-- Subscription & Billing Card -->
            <b-card no-body class="shadow-sm border-0 mb-4">
                <div class="card-header bg-transparent border-bottom py-3">
                    <h5 class="font-weight-bold text-dark mb-0">
                        <i class="bi bi-credit-card-2-front-fill text-primary mr-2"></i>Subscription & Billing
                    </h5>
                </div>
                <b-card-body class="p-4">
                    <div class="d-flex flex-wrap justify-content-between align-items-center mb-3">
                        <div class="mr-md-3" style="flex: 1 1 260px; min-width: 0;">
                            <div class="d-flex align-items-center mb-1">
                                <span class="h5 font-weight-bold text-dark mb-0 mr-2">{{ currentPlanName }}</span>
                                <span v-if="isBetaUser" class="badge badge-beta-amber px-2 py-1 text-uppercase small">Beta Active</span>
                                <span v-else-if="workspace && workspace.isTrialing" class="badge badge-warning px-2 py-1 text-uppercase small">
                                    <i class="fa-solid fa-clock mr-1"></i>Trial: {{ daysRemainingInTrial }}d left
                                </span>
                                <span v-else-if="planTier > 0" class="badge badge-primary px-2 py-1 text-uppercase small">Active</span>
                                <span v-else class="badge badge-secondary px-2 py-1 text-uppercase small">Free Tier</span>
                            </div>
                            <div v-if="isBetaUser" class="text-muted small">
                                Private Beta Plan on dedicated beta environment. All features unlocked with hard system limits.
                            </div>
                            <div v-else-if="planTier === 0" class="text-muted small">
                                No active paid subscription. Includes 1 project, up to 5 platform actions/day, and 5 email reminders/month.
                            </div>
                            <div v-else class="text-muted small">
                                Active subscription for workspace <strong>{{ workspace ? workspace.name : 'Personal' }}</strong>.
                                <span v-if="workspace && workspace.seatLimit"> Seat limit: {{ workspace.seatLimit }}.</span>
                            </div>
                        </div>
                        <div class="mt-3 mt-md-0 ml-md-auto flex-shrink-0 d-flex flex-wrap align-items-center">
                            <b-button v-if="isBetaUser" variant="outline-primary" size="sm" to="/plans">
                                <i class="bi bi-info-circle mr-1"></i>View Plan Details
                            </b-button>
                            <b-button v-else-if="planTier === 0" variant="primary" to="/plans" class="shadow-sm">
                                <i class="fa-solid fa-bolt mr-1"></i>Upgrade to Pro Plan
                            </b-button>
                            <template v-else>

                                <b-button variant="outline-secondary" size="sm" class="mr-2" :disabled="loadingPortal" @click="openCustomerPortal">
                                    <b-spinner v-if="loadingPortal" small class="mr-1"></b-spinner>
                                    <i class="bi bi-box-arrow-up-right mr-1"></i>Customer Portal
                                </b-button>
                                <b-button variant="outline-primary" size="sm" to="/plans">
                                    <i class="bi bi-arrow-up-circle mr-1"></i>Manage Subscription / Change Plan
                                </b-button>
                            </template>
                        </div>
                    </div>

                    <b-row class="mt-3 pt-3 border-top">
                        <b-col sm="6" md="3" class="mb-2">
                            <div class="text-muted small">Workspace</div>
                            <div class="font-weight-bold text-dark">{{ workspace ? workspace.name : 'Default Workspace' }}</div>
                        </b-col>
                        <b-col sm="6" md="3" class="mb-2">
                            <div class="text-muted small">Role in Workspace</div>
                            <div class="font-weight-bold text-dark">{{ workspaceRoleName }}</div>
                        </b-col>
                        <b-col sm="6" md="3" class="mb-2">
                            <div class="text-muted small">Connected Projects Limit</div>
                            <div class="font-weight-bold text-dark">{{ isBetaUser ? 'Up to 20 Projects (System Limit)' : (planTier === 0 ? '1 Project' : (workspace && workspace.maxProjectSpaces ? `Up to ${workspace.maxProjectSpaces} Projects` : 'Up to 5 Projects')) }}</div>
                        </b-col>
                        <b-col sm="6" md="3" class="mb-2">
                            <div class="text-muted small">Platform Connections</div>
                            <div class="font-weight-bold text-dark">{{ isBetaUser ? 'Up to 10 Connections (System Limit)' : (planTier === 0 ? '1 Connection' : (workspace && workspace.maxPlatformConnections ? `Up to ${workspace.maxPlatformConnections} Connections` : 'Up to 3 Connections')) }}</div>
                        </b-col>
                    </b-row>
                </b-card-body>
            </b-card>

            <!-- Password & Sign-In Security Card -->
            <b-card id="card-password-security" no-body class="shadow-sm border-0 mb-4" data-test="card-password-security">
                <div class="card-header bg-transparent border-bottom py-3 d-flex justify-content-between align-items-center flex-wrap">
                    <h5 class="font-weight-bold text-dark mb-0">
                        <i class="bi bi-key-fill text-primary mr-2"></i>Password &amp; Sign-In Security
                    </h5>
                    <b-badge v-if="isDemo" variant="secondary" class="px-2 py-1" data-test="badge-password-status">
                        <i class="bi bi-play-circle-fill mr-1"></i>Demo Account (Read-Only)
                    </b-badge>
                    <b-badge v-else-if="hasAccountPassword" variant="success" class="px-2 py-1" data-test="badge-password-status">
                        <i class="bi bi-shield-lock-fill mr-1"></i>Password Configured
                    </b-badge>
                    <b-badge v-else variant="info" class="px-2 py-1" data-test="badge-password-status">
                        <i class="bi bi-google mr-1"></i>SSO Only (No Password Set)
                    </b-badge>
                </div>
                <b-card-body class="p-4">
                    <div v-if="isDemo" class="alert alert-info py-2 px-3 small mb-3" data-test="alert-demo-password-readonly">
                        <i class="bi bi-info-circle-fill mr-2"></i>
                        Password changes are disabled for shared Interactive Demo accounts so other sandbox visitors can continue exploring.
                    </div>
                    <p v-else-if="hasAccountPassword" class="text-muted small mb-3">
                        Update the password used for direct email and password sign-in on your account (<strong>{{ profileData.email || account.email }}</strong>).
                    </p>
                    <p v-else class="text-muted small mb-3">
                        Your account currently signs in via Single Sign-On (SSO). Set a password below if you also want to sign in directly with your email and password.
                    </p>

                    <b-form @submit.prevent="changePassword">
                        <b-row>
                            <b-col v-if="hasAccountPassword" md="4" class="mb-3">
                                <b-form-group label="Current Password" label-for="inputCurrentPassword" description="Enter your existing account password.">
                                    <b-input-group>
                                        <b-form-input
                                            id="inputCurrentPassword"
                                            v-model="passwordForm.currentPassword"
                                            :type="showCurrentPassword ? 'text' : 'password'"
                                            autocomplete="current-password"
                                            placeholder="Current password"
                                            :disabled="isDemo || changingPassword"
                                            data-test="input-current-password"
                                        ></b-form-input>
                                        <b-input-group-append>
                                            <b-button
                                                variant="outline-secondary"
                                                type="button"
                                                @click="showCurrentPassword = !showCurrentPassword"
                                                :disabled="isDemo || changingPassword"
                                                :title="showCurrentPassword ? 'Hide current password' : 'Show current password'"
                                                :aria-label="showCurrentPassword ? 'Hide current password' : 'Show current password'"
                                                data-test="btn-toggle-current-password"
                                            >
                                                <i :class="showCurrentPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                                            </b-button>
                                        </b-input-group-append>
                                    </b-input-group>
                                </b-form-group>
                            </b-col>

                            <b-col :md="hasAccountPassword ? 4 : 6" class="mb-3">
                                <b-form-group label="New Password" label-for="inputNewPassword" description="Must be at least 6 characters.">
                                    <b-input-group>
                                        <b-form-input
                                            id="inputNewPassword"
                                            v-model="passwordForm.newPassword"
                                            :type="showNewPassword ? 'text' : 'password'"
                                            autocomplete="new-password"
                                            placeholder="At least 6 characters"
                                            :state="newPasswordValidationState"
                                            :disabled="isDemo || changingPassword"
                                            data-test="input-new-password"
                                        ></b-form-input>
                                        <b-input-group-append>
                                            <b-button
                                                variant="outline-secondary"
                                                type="button"
                                                @click="showNewPassword = !showNewPassword"
                                                :disabled="isDemo || changingPassword"
                                                :title="showNewPassword ? 'Hide new password' : 'Show new password'"
                                                :aria-label="showNewPassword ? 'Hide new password' : 'Show new password'"
                                                data-test="btn-toggle-new-password"
                                            >
                                                <i :class="showNewPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                                            </b-button>
                                        </b-input-group-append>
                                    </b-input-group>
                                    <div v-if="newPasswordValidationState === false" class="invalid-feedback d-block small" data-test="feedback-new-password">
                                        New password must be at least 6 characters long.
                                    </div>
                                </b-form-group>
                            </b-col>

                            <b-col :md="hasAccountPassword ? 4 : 6" class="mb-3">
                                <b-form-group label="Confirm New Password" label-for="inputConfirmPassword" description="Re-enter your new password to confirm.">
                                    <b-input-group>
                                        <b-form-input
                                            id="inputConfirmPassword"
                                            v-model="passwordForm.confirmPassword"
                                            :type="showConfirmPassword ? 'text' : 'password'"
                                            autocomplete="new-password"
                                            placeholder="Re-enter new password"
                                            :state="confirmPasswordValidationState"
                                            :disabled="isDemo || changingPassword"
                                            data-test="input-confirm-password"
                                        ></b-form-input>
                                        <b-input-group-append>
                                            <b-button
                                                variant="outline-secondary"
                                                type="button"
                                                @click="showConfirmPassword = !showConfirmPassword"
                                                :disabled="isDemo || changingPassword"
                                                :title="showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'"
                                                :aria-label="showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'"
                                                data-test="btn-toggle-confirm-password"
                                            >
                                                <i :class="showConfirmPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                                            </b-button>
                                        </b-input-group-append>
                                    </b-input-group>
                                    <div v-if="confirmPasswordValidationState === false" class="invalid-feedback d-block small" data-test="feedback-confirm-password">
                                        New password and confirmation do not match.
                                    </div>
                                </b-form-group>
                            </b-col>
                        </b-row>

                        <div class="d-flex justify-content-end">
                            <b-button
                                type="submit"
                                variant="primary"
                                :disabled="isDemo || changingPassword || !canSubmitPasswordChange"
                                data-test="btn-change-password"
                                :data-state="changingPassword ? 'loading' : 'idle'"
                            >
                                <b-spinner v-if="changingPassword" small class="mr-1"></b-spinner>
                                <i v-else class="bi bi-key mr-1"></i>
                                {{ hasAccountPassword ? 'Update Password' : 'Set Password' }}
                            </b-button>
                        </div>
                    </b-form>
                </b-card-body>
            </b-card>

            <!-- Legal, Terms & Compliance Card -->
            <b-card no-body class="shadow-sm border-0 mb-4" data-test="card-legal-compliance">
                <div class="card-header bg-transparent border-bottom py-3 d-flex justify-content-between align-items-center">
                    <h5 class="font-weight-bold text-dark mb-0">
                        <i class="bi bi-shield-check text-primary mr-2"></i>Legal, Terms &amp; Compliance
                    </h5>
                    <b-badge variant="light" class="text-muted border px-2 py-1">
                        GDPR &bull; TLS 1.3
                    </b-badge>
                </div>
                <b-card-body class="p-4">
                    <p class="text-muted small mb-3">
                        Review our terms of service, customer privacy standards, and security policies governing VelocyTrack operations and your connected engineering platforms.
                    </p>
                    <b-row>
                        <b-col md="4" class="mb-3">
                            <div class="p-3 border rounded h-100 legal-policy-card d-flex flex-column justify-content-between">
                                <div>
                                    <div class="d-flex align-items-center font-weight-bold text-dark mb-2">
                                        <i class="bi bi-file-earmark-text text-primary mr-2"></i>
                                        <span>Terms of Service</span>
                                    </div>
                                    <p class="text-muted small mb-3">
                                        Platform usage terms, billing terms via Creem (Merchant of Record), developer cockpit action waivers, and liability caps.
                                    </p>
                                </div>
                                <div class="d-flex align-items-center justify-content-between pt-2 border-top">
                                    <b-button size="sm" variant="outline-primary" @click="openLegal('terms')" data-test="btn-open-tos-modal">
                                        <i class="bi bi-window mr-1"></i>Quick View
                                    </b-button>
                                    <router-link to="/terms" class="small text-decoration-none text-muted font-weight-bold" data-test="link-tos-page">
                                        Full Page <i class="bi bi-box-arrow-up-right ml-1"></i>
                                    </router-link>
                                </div>
                            </div>
                        </b-col>
                        <b-col md="4" class="mb-3">
                            <div class="p-3 border rounded h-100 legal-policy-card d-flex flex-column justify-content-between">
                                <div>
                                    <div class="d-flex align-items-center font-weight-bold text-dark mb-2">
                                        <i class="bi bi-shield-lock text-success mr-2"></i>
                                        <span>Privacy Policy</span>
                                    </div>
                                    <p class="text-muted small mb-3">
                                        GDPR &amp; CCPA compliance, Data Controller vs. Processor roles, AES-256 token encryption, and strict non-storage of source code.
                                    </p>
                                </div>
                                <div class="d-flex align-items-center justify-content-between pt-2 border-top">
                                    <b-button size="sm" variant="outline-success" @click="openLegal('privacy')" data-test="btn-open-privacy-modal">
                                        <i class="bi bi-window mr-1"></i>Quick View
                                    </b-button>
                                    <router-link to="/privacy" class="small text-decoration-none text-muted font-weight-bold" data-test="link-privacy-page">
                                        Full Page <i class="bi bi-box-arrow-up-right ml-1"></i>
                                    </router-link>
                                </div>
                            </div>
                        </b-col>
                        <b-col md="4" class="mb-3">
                            <div class="p-3 border rounded h-100 legal-policy-card d-flex flex-column justify-content-between">
                                <div>
                                    <div class="d-flex align-items-center font-weight-bold text-dark mb-2">
                                        <i class="bi bi-shield-check text-info mr-2"></i>
                                        <span>Acceptable Use &amp; Security</span>
                                    </div>
                                    <p class="text-muted small mb-3">
                                        Rules on token least privilege scoping, rate limiting integrity, prohibition of reverse engineering, and vulnerability disclosure.
                                    </p>
                                </div>
                                <div class="d-flex align-items-center justify-content-between pt-2 border-top">
                                    <b-button size="sm" variant="outline-info" @click="openLegal('security')" data-test="btn-open-security-modal">
                                        <i class="bi bi-window mr-1"></i>Quick View
                                    </b-button>
                                    <router-link to="/acceptable-use" class="small text-decoration-none text-muted font-weight-bold" data-test="link-security-page">
                                        Full Page <i class="bi bi-box-arrow-up-right ml-1"></i>
                                    </router-link>
                                </div>
                            </div>
                        </b-col>
                    </b-row>
                    <div class="small text-muted mt-2 d-flex align-items-center justify-content-between flex-wrap">
                        <span>
                            <i class="bi bi-info-circle mr-1"></i> For data protection inquiries or enterprise legal requests: 
                            <a href="mailto:privacy@velocytrack.com" class="text-primary text-decoration-none">privacy@velocytrack.com</a>
                        </span>
                        <router-link to="/legal" class="text-primary small font-weight-bold text-decoration-none mt-2 mt-sm-0" data-test="link-all-legal-hub">
                            View All Legal Documents <i class="bi bi-arrow-right ml-1"></i>
                        </router-link>
                    </div>
                </b-card-body>
            </b-card>

            <!-- Danger Zone Card -->
            <b-card no-body class="shadow-sm border-danger mb-4">
                <div class="card-header bg-danger text-white py-3">
                    <h5 class="font-weight-bold mb-0">
                        <i class="bi bi-exclamation-octagon-fill mr-2"></i>Danger Zone: Account Deletion
                    </h5>
                </div>
                <b-card-body class="p-4">
                    <div class="d-flex flex-wrap justify-content-between align-items-center">
                        <div class="mr-3 mb-3 mb-md-0" style="flex: 1 1 280px; min-width: 0; max-width: 700px;">
                            <h6 class="font-weight-bold text-danger mb-1">Permanently Delete Your Account</h6>
                            <p class="text-muted small mb-0">
                                This executes your GDPR Right to Erasure. Your personal access token associations, identity records, and configurations will be irreversibly erased from the system.
                            </p>
                        </div>
                        <div class="ml-md-auto flex-shrink-0">
                            <b-button variant="danger" data-test="btn-trigger-delete-account" @click="showDeleteModal = true">
                                <i class="bi bi-trash-fill mr-1"></i>Delete Account
                            </b-button>
                        </div>
                    </div>
                </b-card-body>
            </b-card>

            <!-- Delete Confirmation Modal -->
            <b-modal v-model="showDeleteModal" data-test="modal-delete-account" title="Confirm Permanent Account Deletion" centered header-bg-variant="danger" header-text-variant="white">
                <p>Are you absolutely sure you want to delete your account? This action <strong>cannot be undone</strong>.</p>
                <b-alert show variant="danger" class="small">
                    All your stored preferences, personal access tokens, and profile data will be permanently wiped.
                </b-alert>
                <b-form-group label="Type DELETE to confirm:" label-for="deleteConfirmInput">
                    <b-form-input id="deleteConfirmInput" data-test="input-delete-confirm" v-model="deleteConfirmText" placeholder="DELETE"></b-form-input>
                </b-form-group>
                <template #modal-footer>
                    <b-button variant="secondary" data-test="btn-cancel-delete-account" @click="cancelDeleteModal">Cancel</b-button>
                    <b-button variant="danger" data-test="btn-confirm-delete-account" :data-state="isDeleting ? 'loading' : 'idle'" :disabled="deleteConfirmText !== 'DELETE' || isDeleting" @click="confirmDeleteAccount">
                        <b-spinner v-if="isDeleting" small class="mr-1"></b-spinner>
                        Permanently Delete
                    </b-button>
                </template>
            </b-modal>
        </b-container>
    </div>
</template>

<script>
    import accountMixin from '../mixinAccount';
    import toastMixin from '../mixinToast';
    import { detectUserLocale, inferCountryFromTimezone } from '../localeHelper';
    import eventBus from '../event-bus';

    export default {
        name: 'PageProfile',
        mixins: [accountMixin, toastMixin],
        data() {
            return {
                profileData: {},
                selectedTheme: this.$store?.state?.theme || 'dark',
                selectedCountryCode: this.$store?.state?.countryCode || 'BG',
                selectedTimezone: this.$store?.state?.timezone || 'UTC',
                showDeleteModal: false,
                deleteConfirmText: '',
                isDeleting: false,
                loadingPortal: false,
                uploadingAvatar: false,

                countryOptions: [
                    { value: 'BG', text: 'Bulgaria (BG)' },
                    { value: 'US', text: 'United States (US)' },
                    { value: 'GB', text: 'United Kingdom (GB)' },
                    { value: 'DE', text: 'Germany (DE)' },
                    { value: 'FR', text: 'France (FR)' },
                    { value: 'ES', text: 'Spain (ES)' },
                    { value: 'IT', text: 'Italy (IT)' },
                    { value: 'NL', text: 'Netherlands (NL)' },
                    { value: 'PL', text: 'Poland (PL)' },
                    { value: 'RO', text: 'Romania (RO)' },
                    { value: 'CA', text: 'Canada (CA)' },
                    { value: 'AU', text: 'Australia (AU)' }
                ],
                timezoneOptions: [
                    { value: 'Europe/Sofia', text: 'Europe/Sofia (EET / EEST)' },
                    { value: 'UTC', text: 'UTC (Coordinated Universal Time)' },
                    { value: 'Europe/London', text: 'Europe/London (GMT / BST)' },
                    { value: 'Europe/Berlin', text: 'Europe/Berlin (CET / CEST)' },
                    { value: 'Europe/Paris', text: 'Europe/Paris (CET / CEST)' },
                    { value: 'America/New_York', text: 'America/New_York (EST / EDT)' },
                    { value: 'America/Chicago', text: 'America/Chicago (CST / CDT)' },
                    { value: 'America/Denver', text: 'America/Denver (MST / MDT)' },
                    { value: 'America/Los_Angeles', text: 'America/Los_Angeles (PST / PDT)' },
                    { value: 'Asia/Tokyo', text: 'Asia/Tokyo (JST)' },
                    { value: 'Australia/Sydney', text: 'Australia/Sydney (AEST / AEDT)' }
                ],
                startLogDate: new Date(Date.now() - 14 * 86400000).toISOString().slice(0, 10),
                dailyCapacity: 8,
                defaultRole: 0,
                inboxAssignedDays: 14,
                savingWorkDefaults: false,
                enableBehindAlert: false,
                customBehindHours: 8,
                reminderSettings: {
                    enableDailyReminder: false,
                    enableWeeklyReminder: false,
                    behindHoursThreshold: 0,
                    reminderHourLocal: 17,
                    reminderTimezone: null,
                    monthlyRemindersUsed: 0,
                    monthlyRemindersLimit: null,
                    monthlyRemindersRemaining: null
                },
                savingReminders: false,
                loadingReminders: false,
                passwordForm: {
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                },
                changingPassword: false,
                showCurrentPassword: false,
                showNewPassword: false,
                showConfirmPassword: false,
                reminderHourOptions: [
                    { value: 12, text: '12:00 PM (12:00) - Midday' },
                    { value: 15, text: '3:00 PM (15:00)' },
                    { value: 16, text: '4:00 PM (16:00)' },
                    { value: 17, text: '5:00 PM (17:00) - Default' },
                    { value: 18, text: '6:00 PM (18:00)' },
                    { value: 19, text: '7:00 PM (19:00)' },
                    { value: 20, text: '8:00 PM (20:00)' }
                ]
            };
        },
        computed: {
            currentPlanName() {
                if (this.isBetaUser) return 'Beta Plan';
                const map = { 0: 'Free', 1: 'Pro Plan', 2: 'Team Plan', 3: 'Enterprise' };
                return map[this.planTier] || 'Free';
            },
            workspaceRoleName() {
                if (!this.workspace) return 'Owner';
                const role = this.workspace.currentUserRole;
                if (role === 0 || role === 'Owner') return 'Owner';
                if (role === 1 || role === 'Admin') return 'Admin';
                if (role === 2 || role === 'Member') return 'Member';
                return 'Member';
            },
            hasAnyReminderActive() {
                return !!(
                    this.reminderSettings?.enableDailyReminder ||
                    this.reminderSettings?.enableWeeklyReminder ||
                    this.enableBehindAlert
                );
            },
            hasReminderMonthlyQuota() {
                if (this.hasPrivilegeBypass) return false;
                if (this.workspace && this.workspace.isTrialing) return false;
                if (typeof this.reminderSettings?.monthlyRemindersLimit === 'number') {
                    return this.reminderSettings.monthlyRemindersLimit > 0;
                }
                return this.planTier === 0;
            },
            remindersLimitThisMonth() {
                if (!this.hasReminderMonthlyQuota) return null;
                return typeof this.reminderSettings?.monthlyRemindersLimit === 'number'
                    ? this.reminderSettings.monthlyRemindersLimit
                    : 5;
            },
            remindersUsedThisMonth() {
                if (!this.hasReminderMonthlyQuota) return 0;
                return typeof this.reminderSettings?.monthlyRemindersUsed === 'number'
                    ? this.reminderSettings.monthlyRemindersUsed
                    : 0;
            },
            remindersRemainingThisMonth() {
                if (!this.hasReminderMonthlyQuota) return null;
                if (typeof this.reminderSettings?.monthlyRemindersRemaining === 'number') {
                    return this.reminderSettings.monthlyRemindersRemaining;
                }
                return Math.max(0, (this.remindersLimitThisMonth || 5) - this.remindersUsedThisMonth);
            },
            isReminderQuotaExhausted() {
                return this.hasReminderMonthlyQuota && this.remindersRemainingThisMonth <= 0;
            },
            profileAvatarUrl() {
                return this.profileData?.picture || this.account?.picture || '';
            },
            hasCustomAvatar() {
                return !!(this.profileData?.picture || this.account?.picture);
            },
            avatarInitials() {
                const name = this.profileData?.fullName || this.account?.fullName || 'User';
                return name.split(' ').map(p => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
            },
            hasAccountPassword() {
                if (typeof this.profileData?.hasPassword === 'boolean') {
                    return this.profileData.hasPassword;
                }
                if (typeof this.account?.hasPassword === 'boolean') {
                    return this.account.hasPassword;
                }
                return true;
            },
            newPasswordValidationState() {
                const np = this.passwordForm?.newPassword || '';
                if (!np) return null;
                return np.length >= 6;
            },
            confirmPasswordValidationState() {
                const cp = this.passwordForm?.confirmPassword || '';
                if (!cp) return null;
                return cp === (this.passwordForm?.newPassword || '');
            },
            canSubmitPasswordChange() {
                const np = this.passwordForm?.newPassword || '';
                const cp = this.passwordForm?.confirmPassword || '';
                if (np.length < 6 || cp !== np) return false;
                if (this.hasAccountPassword && !(this.passwordForm?.currentPassword || '').length) {
                    return false;
                }
                return true;
            }
        },
        watch: {
            enableBehindAlert(val) {
                if (val && (!this.customBehindHours || this.customBehindHours <= 0)) {
                    this.customBehindHours = 8;
                }
            }
        },
        async mounted() {
            await Promise.all([
                this.loadProfileAsync(),
                this.loadProfileSettings(),
                this.loadReminderSettings()
            ]);
            this.syncFromStore();
            if (this.$route.query.tab === 'reminders') {
                this.$nextTick(() => {
                    const el = document.getElementById('card-reminders');
                    if (el) {
                        el.scrollIntoView({ behavior: 'smooth' });
                    }
                });
            }
        },
        methods: {
            openLegal(tab = 'terms') {
                eventBus.$emit('open-legal', tab);
            },
            async loadProfileSettings() {
                try {
                    const resp = await this.$http.get('/Account/ProfileSettings');
                    if (resp && resp.data) {
                        if (resp.data.startLogDate) {
                            this.startLogDate = resp.data.startLogDate.slice(0, 10);
                        } else {
                            this.startLogDate = new Date(Date.now() - 14 * 86400000).toISOString().slice(0, 10);
                        }
                        if (resp.data.dailyCapacity > 0) {
                            this.dailyCapacity = resp.data.dailyCapacity;
                        }
                        if (resp.data.defaultRole !== undefined && resp.data.defaultRole !== null) {
                            this.defaultRole = this.normalizeRole(resp.data.defaultRole);
                        }
                        if (resp.data.inboxAssignedDays >= 1 && resp.data.inboxAssignedDays <= 30) {
                            this.inboxAssignedDays = resp.data.inboxAssignedDays;
                        }
                    }
                } catch (err) {
                    console.debug('Failed to load profile settings:', err);
                }
            },
            async saveWorkDefaults() {
                if (!this.startLogDate) {
                    this.toastError('Validation', 'Start Log Date is required.');
                    return;
                }
                const cap = parseInt(this.dailyCapacity, 10);
                if (isNaN(cap) || cap < 1 || cap > 12) {
                    this.toastError('Validation', 'Daily Capacity must be between 1 and 12 hours.');
                    return;
                }
                const inboxDays = parseInt(this.inboxAssignedDays, 10);
                if (isNaN(inboxDays) || inboxDays < 1 || inboxDays > 30) {
                    this.toastError('Validation', 'Discussions Lookback Period must be between 1 and 30 days.');
                    return;
                }

                this.savingWorkDefaults = true;
                try {
                    await this.$http.post('/Account/SaveProfileSettings', {
                        startLogDate: this.startLogDate,
                        dailyCapacity: cap,
                        defaultRole: this.normalizeRole(this.defaultRole),
                        inboxAssignedDays: inboxDays
                    });
                    this.toastSuccess('Settings saved', 'Work defaults updated successfully.');
                } catch (err) {
                    this.toastError('Save failed', err?.response?.data?.message || 'Could not save work defaults.');
                } finally {
                    this.savingWorkDefaults = false;
                }
            },
            async loadReminderSettings() {
                this.loadingReminders = true;
                try {
                    const resp = await this.$http.get('/Account/ReminderSettings');
                    if (resp && resp.data) {
                        const threshold = resp.data.behindHoursThreshold || 0;
                        this.enableBehindAlert = threshold > 0;
                        this.customBehindHours = threshold > 0 ? threshold : 8;
                        this.reminderSettings = {
                            enableDailyReminder: !!resp.data.enableDailyReminder,
                            enableWeeklyReminder: !!resp.data.enableWeeklyReminder,
                            behindHoursThreshold: threshold,
                            reminderHourLocal: resp.data.reminderHourLocal >= 0 ? resp.data.reminderHourLocal : 17,
                            reminderTimezone: resp.data.reminderTimezone || this.selectedTimezone,
                            monthlyRemindersUsed: resp.data.monthlyRemindersUsed ?? 0,
                            monthlyRemindersLimit: resp.data.monthlyRemindersLimit ?? null,
                            monthlyRemindersRemaining: resp.data.monthlyRemindersRemaining ?? null
                        };
                    }
                } catch (err) {
                    console.debug('Failed to load reminder settings:', err);
                } finally {
                    this.loadingReminders = false;
                }
            },
            async saveReminderSettings() {
                if (this.enableBehindAlert) {
                    const hrs = parseInt(this.customBehindHours, 10);
                    if (isNaN(hrs) || hrs < 1 || hrs > 160) {
                        this.toastError('Validation', 'Behind-Hours Alert threshold must be between 1 and 160 hours.');
                        return;
                    }
                    this.reminderSettings.behindHoursThreshold = hrs;
                } else {
                    this.reminderSettings.behindHoursThreshold = 0;
                }

                this.savingReminders = true;
                try {
                    this.reminderSettings.reminderTimezone = this.selectedTimezone;
                    const resp = await this.$http.post('/Account/SaveReminderSettings', this.reminderSettings);
                    if (resp && resp.data) {
                        this.reminderSettings.monthlyRemindersUsed = resp.data.monthlyRemindersUsed ?? this.reminderSettings.monthlyRemindersUsed;
                        this.reminderSettings.monthlyRemindersLimit = resp.data.monthlyRemindersLimit ?? this.reminderSettings.monthlyRemindersLimit;
                        this.reminderSettings.monthlyRemindersRemaining = resp.data.monthlyRemindersRemaining ?? this.reminderSettings.monthlyRemindersRemaining;
                    }
                    this.toastSuccess('Reminders Saved', 'Your time log reminder preferences have been saved.');
                } catch (err) {
                    this.toastError('Save Failed', err?.response?.data?.message || 'Could not save reminder preferences.');
                } finally {
                    this.savingReminders = false;
                }
            },
            syncFromStore() {
                this.selectedTheme = this.$store?.state?.theme || 'dark';
                this.selectedCountryCode = this.$store?.state?.countryCode || 'BG';
                this.selectedTimezone = this.$store?.state?.timezone || 'UTC';
            },
            async loadProfileAsync() {
                try {
                    const resp = await this.$http.get('/Account/Me');
                    if (resp && resp.data) {
                        this.profileData = resp.data;
                        this.$store.commit('setAccount', { ...this.account, ...resp.data });
                    }
                } catch (err) {
                    console.debug('Could not load profile from /Account/Me:', err);
                }
            },
            async changePassword() {
                if (this.isDemo) {
                    this.toastWarning('Demo Mode', 'Password changes are disabled for shared Interactive Demo accounts.');
                    return;
                }
                if (this.hasAccountPassword && !this.passwordForm.currentPassword) {
                    this.toastError('Validation', 'Current password is required.');
                    return;
                }
                if (!this.passwordForm.newPassword || this.passwordForm.newPassword.length < 6) {
                    this.toastError('Validation', 'New password must be at least 6 characters long.');
                    return;
                }
                if (this.passwordForm.newPassword !== this.passwordForm.confirmPassword) {
                    this.toastError('Validation', 'New password and confirmation do not match.');
                    return;
                }

                this.changingPassword = true;
                try {
                    const resp = await this.$http.post('/Account/ChangePassword', {
                        currentPassword: this.passwordForm.currentPassword,
                        newPassword: this.passwordForm.newPassword,
                        confirmPassword: this.passwordForm.confirmPassword
                    });
                    this.passwordForm.currentPassword = '';
                    this.passwordForm.newPassword = '';
                    this.passwordForm.confirmPassword = '';
                    this.showCurrentPassword = false;
                    this.showNewPassword = false;
                    this.showConfirmPassword = false;
                    this.profileData = { ...this.profileData, hasPassword: true };
                    this.$store.commit('setAccount', { ...this.account, hasPassword: true });
                    this.toastSuccess('Password Updated', resp?.data?.message || 'Your password has been updated successfully.');
                } catch (err) {
                    const msg = err?.response?.data?.message || err?.message || 'Could not update password.';
                    this.toastError('Password Update Failed', msg);
                } finally {
                    this.changingPassword = false;
                }
            },
            triggerAvatarUpload() {
                if (this.$refs.avatarFileInput) {
                    this.$refs.avatarFileInput.click();
                }
            },
            async onAvatarFileSelected(event) {
                const file = event?.target?.files?.[0];
                if (!file) return;

                if (!file.type.startsWith('image/')) {
                    this.toastError('Invalid File', 'Please select an image file (PNG, JPG, WebP, or GIF).');
                    if (event?.target) event.target.value = '';
                    return;
                }

                if (file.size > 2 * 1024 * 1024) {
                    this.toastError('File Too Large', 'Image size exceeds 2MB limit. Please choose a smaller image.');
                    if (event?.target) event.target.value = '';
                    return;
                }

                this.uploadingAvatar = true;
                const reader = new FileReader();
                reader.onload = async (e) => {
                    try {
                        const dataUrl = e.target.result;
                        const resp = await this.$http.post('/Account/UploadAvatar', {
                            avatarDataUrl: dataUrl
                        });
                        const updatedPic = resp?.data?.picture || dataUrl;
                        this.profileData = { ...this.profileData, picture: updatedPic };
                        this.$store.commit('setAccount', { ...this.account, picture: updatedPic });
                        this.toastSuccess('Avatar Updated', 'Your profile picture has been updated successfully.');
                    } catch (err) {
                        this.toastError('Upload Failed', err?.response?.data?.message || err?.response?.data || 'Failed to upload avatar.');
                    } finally {
                        this.uploadingAvatar = false;
                        if (event?.target) event.target.value = '';
                    }
                };
                reader.onerror = () => {
                    this.uploadingAvatar = false;
                    this.toastError('Error', 'Failed to read image file.');
                    if (event?.target) event.target.value = '';
                };
                reader.readAsDataURL(file);
            },
            async removeAvatar() {
                this.uploadingAvatar = true;
                try {
                    await this.$http.delete('/Account/Avatar');
                    this.profileData = { ...this.profileData, picture: '' };
                    this.$store.commit('setAccount', { ...this.account, picture: '' });
                    this.toastSuccess('Avatar Removed', 'Your avatar has been reset to default.');
                } catch (err) {
                    this.toastError('Failed', 'Failed to remove avatar.');
                } finally {
                    this.uploadingAvatar = false;
                }
            },
            changeTheme(theme) {
                this.selectedTheme = theme;
                this.$store.commit('setTheme', theme);
                document.documentElement.setAttribute('data-theme', theme);
                this.toastSuccess('Theme updated', `Switched to ${theme} theme.`);
            },
            onCountryChange(code, options = {}) {
                const silent = options && options.silent === true;
                this.$store.commit('setCountryCode', code);
                if (!silent) {
                    this.toastSuccess('Region updated', `Public holidays will now load for ${code}.`);
                }
            },
            onTimezoneChange(tz, options = {}) {
                const silent = options && options.silent === true;
                this.$store.commit('setTimezone', tz);
                if (!silent) {
                    this.toastSuccess('Timezone updated', `Timezone set to ${tz}.`);
                }
            },
            inferCountryFromTimezone(tz) {
                return inferCountryFromTimezone(tz);
            },
            autoDetectLocale() {
                const detected = detectUserLocale(this.selectedCountryCode || 'BG');
                this.selectedCountryCode = detected.countryCode;
                this.onCountryChange(detected.countryCode, { silent: true });

                if (detected.timezone) {
                    const hasTz = this.timezoneOptions.some(o => o.value === detected.timezone);
                    if (!hasTz) {
                        this.timezoneOptions.unshift({ value: detected.timezone, text: `${detected.timezone} (Detected)` });
                    }
                    this.selectedTimezone = detected.timezone;
                    this.onTimezoneChange(detected.timezone, { silent: true });
                }

                const countryName = this.countryOptions.find(o => o.value === this.selectedCountryCode)?.text || this.selectedCountryCode;
                this.toastSuccess('Locale Auto-Detected', `Country set to ${countryName}, Timezone set to ${this.selectedTimezone}.`);
            },
            openUpgradeModal() {
                this.$router.push('/plans').catch(() => {});
            },
            async openCustomerPortal() {
                this.loadingPortal = true;
                try {
                    const resp = await this.$http.get('/Billing/CustomerPortal');
                    if (resp.data && resp.data.portalUrl) {
                        const url = resp.data.portalUrl;
                        if (url.startsWith('http://') || url.startsWith('https://')) {
                            window.open(url, '_blank');
                        } else {
                            this.$router.push(url);
                        }
                    } else {
                        this.toastInfo('Customer Portal', 'Subscription portal is not active or available for this workspace.');
                    }
                } catch (err) {
                    console.error('Failed to open customer portal:', err);
                    const msg = err.response?.data?.message || err.response?.data?.error || err.message || 'Unable to retrieve customer portal URL at this time.';
                    this.toastError('Portal Unavailable', msg);
                } finally {
                    this.loadingPortal = false;
                }
            },
            cancelDeleteModal() {
                this.showDeleteModal = false;
                this.deleteConfirmText = '';
            },
            async confirmDeleteAccount() {
                if (this.deleteConfirmText !== 'DELETE') return;
                this.isDeleting = true;
                try {
                    await this.$http.delete('/Account/Me');
                    this.toastSuccess('Account Deleted', 'Your account has been deleted.');
                    this.$store.commit('signOut');
                    this.$router.push('/');
                } catch (err) {
                    console.error('Failed to delete account:', err);
                    this.toastError('Deletion Failed', 'Unable to delete account at this time.');
                } finally {
                    this.isDeleting = false;
                    this.showDeleteModal = false;
                    this.deleteConfirmText = '';
                }
            }
        }
    };
</script>

<style scoped>
    .profile-page {
        max-width: 980px;
        margin-left: auto;
        margin-right: auto;
    }
    .profile-page > .container,
    .profile-page > .container-fluid {
        padding-left: 0;
        padding-right: 0;
        max-width: 100% !important;
    }

    .theme-option-card {
        cursor: pointer;
        transition: all 0.2s ease-in-out;
        background-color: var(--vt-card-bg, #fff);
        border-color: var(--vt-border, #dee2e6);
        color: var(--vt-text, #212529);
    }

    .theme-option-card:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    .theme-option-active {
        border-color: #007bff !important;
        border-width: 2px !important;
        background-color: rgba(0, 123, 255, 0.08) !important;
    }

    .theme-preview-box {
        height: 80px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 8px;
    }

    .theme-light-box {
        background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    }

    .theme-dark-box {
        background: linear-gradient(135deg, #1f2933 0%, #121820 100%);
    }

    .border-danger {
        border-color: #dc3545 !important;
    }

    .beta-status-card {
        background-color: rgba(245, 158, 11, 0.08);
        border-color: rgba(245, 158, 11, 0.4) !important;
    }

    [data-theme="dark"] .beta-status-card {
        background-color: rgba(245, 158, 11, 0.12);
        border-color: rgba(245, 158, 11, 0.5) !important;
    }

    .reminder-switch-box {
        background-color: var(--vt-surface, #f8f9fa);
        border: 1px solid var(--vt-border, #dee2e6);
        transition: border-color 0.2s ease, background-color 0.2s ease;
    }

    [data-theme="dark"] .reminder-switch-box {
        background-color: #1e293b !important;
        border-color: #334155 !important;
    }

    .profile-avatar-wrapper {
        position: relative;
        display: inline-block;
    }

    .profile-avatar-wrapper .profile-avatar {
        transition: filter 0.2s ease;
    }

    .profile-avatar-wrapper:hover .profile-avatar {
        filter: brightness(0.85);
    }

    .avatar-upload-overlay {
        position: absolute;
        bottom: 0;
        right: 0;
        width: 26px;
        height: 26px;
        border-radius: 50%;
        background-color: #007bff;
        color: #ffffff;
        border: 2px solid var(--vt-surface, #ffffff);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.75rem;
        cursor: pointer;
        padding: 0;
        transition: transform 0.15s ease, background-color 0.15s ease;
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
    }

    .avatar-upload-overlay:hover {
        background-color: #0056b3;
        transform: scale(1.1);
    }

    .btn-avatar-action {
        font-size: 0.75rem;
        border-radius: 4px;
        transition: all 0.15s ease;
    }

    .legal-policy-card {
        background-color: var(--vt-surface, #f8f9fa);
        border: 1px solid var(--vt-border, #dee2e6);
        transition: border-color 0.2s ease, background-color 0.2s ease;
    }

    [data-theme="dark"] .legal-policy-card {
        background-color: #1e293b !important;
        border-color: #334155 !important;
    }

    [data-theme="dark"] .legal-policy-card .text-dark {
        color: #f1f5f9 !important;
    }

    @media (max-width: 767.98px) {
        .card-header {
            padding: 0.65rem 0.85rem !important;
        }
        .card-body {
            padding: 0.85rem !important;
            overflow-wrap: anywhere;
            word-break: break-word;
        }
        .card-body .d-flex.align-items-center {
            flex-wrap: wrap;
            min-width: 0;
        }
        .card-body .badge {
            white-space: normal;
            text-align: left;
            word-break: break-all;
        }
        .theme-preview-box {
            height: 60px;
        }
    }

    .profile-mobile-noop {
        color: #f1f5f9 !important;
        /* end */
    }
</style>

